import { defaultAllSelection } from "services/constants/selections";
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Drawer } from "@mui/material";
import {
  APP_BAR_OFFSET,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
  designColors,
} from "services/constants/designTokens";
import { useExpandedContentLeft } from "components/DesignSystem/sharedUiPatterns";
import { useSelector } from "react-redux";
import { customAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import dayjs from "dayjs";
import { BulkReprocessPanel } from "./BulkReprocessPanel";

export const validateReprocess = (companyId, selected, start, end, dataSource = "biometric") => {
  if (!companyId) return "Please select a company.";
  if (!selected.length) return "Please select at least one employee.";
  if (!start || !end || !dayjs(start).isValid() || !dayjs(end).isValid())
    return "Please select a valid date range.";
  if (dayjs(end).isBefore(dayjs(start), "day"))
    return "End date cannot be before start date.";
  if (dayjs(end).isAfter(dayjs(), "day"))
    return "Future dates are not allowed.";
  if (dataSource === "attendance" && !dayjs(end).isBefore(dayjs(), "day"))
    return "Attendance Records can only be reprocessed through yesterday.";
  // "Attendance Records" is a real, selectable option in the UI, but only
  if (!["biometric", "attendance"].includes(dataSource)) return "Please select a valid data source.";
  return "";
};

export const BulkReprocess = ({
  onTaskStart,
  onTaskUpdate,
  onTaskComplete,
  reCallApi,
  onClose,
  setUserProcess,
  initialCompanyId,
  expanded,
  onToggleExpand,
}) => {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { result: companyResult } = useFetch(domain + "get-active-company/");
  const companies = Array.isArray(companyResult) ? companyResult : [];
  const [companyId, setCompanyId] = useState(initialCompanyId || "");
  const [dataSource, setDataSource] = useState("attendance");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  // Attendance Records (the default source) only reprocesses through
  // yesterday, so the initial range must not start on today.
  const [start, setStart] = useState(() => dayjs().subtract(1, "day"));
  const [end, setEnd] = useState(() => dayjs().subtract(1, "day"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const contentLeft = useExpandedContentLeft(expanded);
  const defaultCompanyId = companies.length === 1 ? companies[0].id : null;

  // Lock the page scroll while the expanded (full-page) overlay is up, same
  // as GlobalSidePanel/CompanyDetailPanel — otherwise the page behind this
  // overlay can still scroll on its own, so its scrollbar and this panel's
  // own internal scrollbar both show at once, looking like two vertical
  // scrollbars stacked side by side.
  useEffect(() => {
    if (!expanded) return undefined;
    if (typeof document === "undefined") return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [expanded]);

  // Re-sync whenever the top bar's company changes while this panel is open.
  // Previously this only ran once (via useState's initial value) plus a
  // fallback for the no-topbar-company case, so switching companies from the
  // top bar after opening Bulk Reprocess never updated companyId here — the
  // employee list, and any already-selected employees, silently stayed on
  // the old company. Now every company change resets the loaded employees
  // and any selection, same as the removed in-panel company picker used to.
  useEffect(() => {
    setCompanyId(initialCompanyId || defaultCompanyId || "");
  }, [initialCompanyId, defaultCompanyId]);

  useEffect(() => {
    setUsers([]);
    setSelected([]);
    setError("");
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setUsers([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const fetchEmployees = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          domain +
            "list/employee?company_id=" +
            encodeURIComponent(companyId) +
            `&department_id=${defaultAllSelection}&status=Active`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "token " + token,
            },
            signal: controller.signal,
          },
        );
        if (response.status === 409) {
          sessionHandling();
          throw new Error("Session expired. Please sign in again.");
        }
        if (!response.ok)
          throw new Error("Couldn't load employees. Please try again.");
        const result = await response.json();
        const employees = Array.isArray(result) ? result : result?.results;
        if (!Array.isArray(employees))
          throw new Error("Couldn't load employees. Please try again.");
        if (!controller.signal.aborted) setUsers(employees);
      } catch (failure) {
        if (!controller.signal.aborted) {
          setUsers([]);
          setError(failure.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchEmployees();
    return () => controller.abort();
  }, [companyId, domain, token, retry]);

  const connectTaskWebSocket = (taskId, websocketUrl) => {
    const socket = new WebSocket(websocketUrl);
    socket.onopen = () => {
      console.log("WebSocket connected for task:", taskId);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket progress update:", data);

        // Update parent with progress
        if (onTaskUpdate) {
          onTaskUpdate({
            progress: data.progress || 0,
            message: data.message || "",
            status: data.status || "PROGRESS",
          });
          setUserProcess(true);
        }

        if (data.status === "SUCCESS") {
          showToast("success", "Background processing completed.");

          // Notify parent of completion
          setTimeout(() => {
            if (onTaskComplete) {
              onTaskComplete();
              reCallApi();
            }
            socket.close();
          }, 2000);
        } else if (data.status === "FAILURE") {
          showToast(
            "error",
            `Background processing failed: ${data.error || "Unknown error"}`,
          );
          if (onTaskComplete) {
            onTaskComplete();
          }
          socket.close();
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      showToast("warning", "Connection to progress updates interrupted");
    };

    socket.onclose = () => {
      console.log("WebSocket closed");
    };
  };

  const submit = async () => {
    if (submittingRef.current) return;
    // Both data sources reprocess the chosen date range.
    const validation = validateReprocess(companyId, selected, start, end, dataSource);
    if (validation) {
      showToast("error", validation);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const isAttendance = dataSource === "attendance";
      const response = await customAPI(
        domain + (isAttendance ? "hrzone/bulk-ar-reprocess/" : "punch-reprocess/"),
        "POST",
        {
          user_ids: selected.map((user) => user.user_id ?? user.id),
          ...(isAttendance ? {} : { company_id: companyId }),
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
        },
      );
      if (!response?.ok)
        throw new Error(
          response?.message ||
            "Couldn't reprocess attendance. Please try again.",
        );
      const result = response.responseDetails;
      showToast(
        "success",
        result?.message || "Attendance reprocessing queued successfully.",
      );
      if (result?.task_id) {
        onTaskStart?.({
          categoryName: "Bulk Reprocessing",
          message: result?.message || "Starting background processing...",
          taskId: result.task_id,
          taskType: "update",
        });
        if (result.websocket_url) {
          try {
            connectTaskWebSocket(result.task_id, result.websocket_url);
          } catch {
            showToast(
              "warning",
              "Reprocessing started, but progress updates could not connect.",
            );
          }
        }
      } else {
        onTaskComplete?.();
        reCallApi?.();
      }
      setConfirmOpen(false);
      onClose();
    } catch (failure) {
      showToast("error", failure.message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const panelContent = (
    <BulkReprocessPanel
      companyId={companyId}
      users={users}
      selected={selected}
      onSelectionChange={setSelected}
      loading={loading}
      error={error}
      onRetry={() => setRetry((value) => value + 1)}
      dataSource={dataSource}
      onDataSourceChange={(source) => {
        if (source === "attendance") {
          const yesterday = dayjs().subtract(1, "day");
          if (start.isAfter(yesterday, "day")) setStart(yesterday);
          if (end.isAfter(yesterday, "day")) setEnd(yesterday);
        } else if (source !== dataSource) {
          // Raw Data can include today, so it opens on today's date.
          const today = dayjs();
          setStart(today);
          setEnd(today);
        }
        setDataSource(source);
      }}
      startDate={start}
      endDate={end}
      onDateChange={(rangeStart, rangeEnd) => {
        const first = dayjs(
          rangeStart?.toDate ? rangeStart.toDate() : rangeStart,
        );
        setStart(first);
        setEnd(
          rangeEnd
            ? dayjs(rangeEnd?.toDate ? rangeEnd.toDate() : rangeEnd)
            : first,
        );
      }}
      onSubmit={(event) => {
        event.preventDefault();
        const validation = validateReprocess(companyId, selected, start, end, dataSource);
        if (validation) {
          showToast("error", validation);
          return;
        }
        setConfirmOpen(true);
      }}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      submitting={submitting}
      confirmOpen={confirmOpen}
      onCancelConfirm={() => setConfirmOpen(false)}
      onConfirm={submit}
    />
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          bgcolor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        {/* Every other expanded side panel (GlobalSidePanel, CompOff/Apply.js,
            …) wraps panelContent in this explicit height:100% box before
            rendering it — without it, the panel's own height:100% form can
            fail to resolve against this position:fixed ancestor, which lets
            its inner overflowY:auto region grow past the fixed viewport
            instead of scrolling within it, showing a second, page-level
            scrollbar alongside the panel's own one. */}
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Drawer
      anchor="right"
      open
      sx={panelDrawerSx()}
      onClose={() => {
        if (!submitting) onClose();
      }}
    >
      <Box
        sx={{
          width: PANEL_DRAWER_WIDTH,
          maxWidth: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {panelContent}
      </Box>
    </Drawer>
  );
};
